import { toast } from "react-toastify";

import {
  ActionShippingRequestBodyType,
  ExperienceActionRequestBodyType,
  ExperienceActionRequestPathParamsType,
  ExperienceActionResponseBodyType,
  ExperienceCategory,
  ExperienceDto,
  ShippingActionsRequestPathParamsType,
  ShippingActionsResponseBodyType,
} from "@intelligems/ig-types";
import {
  QueryObserverOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import {
  mpClickDeleteCampaign,
  mpClickDeleteExperience,
} from "../../utils/mixpanel";
import { api } from "../api";
import { useConfiguration } from "../configurations/use-configuration";
import { useSetDuplicateProductStatus } from "../duplicate-products/set-duplicate-product-status";
import { exclusionGroupKeys, experienceKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";
import { useLoggedInUser } from "../user-auths/login";

import { experienceAction } from "./experience-action";

const deleteShippingExperience = async (
  payload: ApiRequest<
    ActionShippingRequestBodyType,
    ShippingActionsRequestPathParamsType
  >
): Promise<ShippingActionsResponseBodyType> => {
  const resp = await api.post(
    `/v2/experiences/${payload.pathParams.experienceId}/shipping/delete`,
    null
  );
  return resp.data as ShippingActionsResponseBodyType;
};
export const useDeleteShippingExperience = (
  options?: QueryObserverOptions<ShippingActionsResponseBodyType, Error>
) => {
  return useMutation<
    ShippingActionsResponseBodyType,
    Error,
    ApiRequest<
      ActionShippingRequestBodyType,
      ShippingActionsRequestPathParamsType
    >
  >({
    mutationKey: ["deleteShippingExperience"],
    mutationFn: async (data) => deleteShippingExperience(data),
    onSuccess: async (data) => {
      if (data.actionRequested) toast.success("Restoring shipping profiles...");
    },
    onError: async () => {
      toast.error("Error restoring shipping profiles.");
    },
  });
};

export const useDeleteExperience = (
  experience?: ExperienceDto,
  category?: ExperienceCategory
) => {
  const queryClient = useQueryClient();
  const archiveDuplicateProducts = useSetDuplicateProductStatus();
  const deleteShippingExperience = useDeleteShippingExperience();
  const user = useLoggedInUser();

  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  );

  return useMutation<
    ExperienceActionResponseBodyType,
    Error,
    ApiRequest<
      ExperienceActionRequestBodyType,
      ExperienceActionRequestPathParamsType
    >
  >({
    mutationFn: async (data) => experienceAction(data),
    onSuccess: async (data, payload) => {
      queryClient.removeQueries({
        queryKey: experienceKeys.one(payload.pathParams.experienceId),
      });

      await queryClient.invalidateQueries({
        queryKey:
          category === "personalization"
            ? experienceKeys.personalizationList
            : experienceKeys.experimentList,
      });
      await queryClient.invalidateQueries({ queryKey: exclusionGroupKeys.all });

      if (
        shouldDuplicateProducts?.value &&
        experience?.testTypes.hasTestPricing
      ) {
        archiveDuplicateProducts.mutate({
          body: {
            experienceId: payload.pathParams.experienceId,
            delayHours: 0,
            status: "archived",
          },
          queryParams: undefined,
          pathParams: undefined,
        });
      }

      if (experience?.testTypes.hasTestShipping) {
        deleteShippingExperience.mutate({
          pathParams: {
            experienceId: experience.id!,
            action: "delete",
          },
          body: undefined,
          queryParams: undefined,
        });
      }

      if (experience) {
        if (category === "experiment") {
          mpClickDeleteExperience(
            experience,
            user.isSuccess && user.data.user && user.data.user.id
              ? user.data.user.id
              : null
          );
        } else if (category === "personalization") {
          mpClickDeleteCampaign(
            experience,
            user.isSuccess && user.data.user && user.data.user.id
              ? user.data.user.id
              : null
          );
        }
      }
    },
  });
};
